import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import Navbar from "../../feature/Navbar";
import Footer from "../../feature/Footer";
import WhatsAppButton from "../../feature/WhatsAppButton";

const WA_NUMBER = "5511923169450";
const STORE_SLUG = "ray_melo";

const LENS_WIDTH = 220;
const LENS_HEIGHT = 160;
const ZOOM = 2.2;

type ProductColor = {
  color: string;
  images: string[];
};

type LensState = {
  visible: boolean;
  left: number;
  top: number;
  mouseX: number;
  mouseY: number;
};

const isVisible = (p: any) => {
  const v = p?.visible;
  return v === true || v === 1 || v === "true" || v === "1";
};

const normalizeSizes = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const getSizes = (product: any): string[] => {
  const fromSizes = normalizeSizes(product?.sizes);
  if (fromSizes.length > 0) return fromSizes;
  const fromSize = normalizeSizes(product?.size);
  if (fromSize.length > 0) return fromSize;
  const fromTamanhos = normalizeSizes(product?.tamanhos);
  if (fromTamanhos.length > 0) return fromTamanhos;
  const fromTamanho = normalizeSizes(product?.tamanho);
  if (fromTamanho.length > 0) return fromTamanho;
  return [];
};

const uniqueStrings = (items: any[]) => {
  return [...new Set(items.filter((item) => typeof item === "string" && item.trim()))];
};

const getMainImage = (product: any): string => {
  if (typeof product?.image_url === "string" && product.image_url.trim()) return product.image_url;
  if (typeof product?.image === "string" && product.image.trim()) return product.image;
  if (Array.isArray(product?.images)) {
    const firstImage = product.images.find((img: any) => typeof img === "string" && img.trim());
    if (firstImage) return firstImage;
  }
  if (Array.isArray(product?.colors)) {
    for (const color of product.colors) {
      if (Array.isArray(color?.images) && color.images.length > 0) {
        const firstColorImage = color.images.find((img: any) => typeof img === "string" && img.trim());
        if (firstColorImage) return firstColorImage;
      }
      if (typeof color?.image === "string" && color.image.trim()) return color.image;
    }
  }
  return "";
};

const getColorOptions = (product: any): ProductColor[] => {
  if (!Array.isArray(product?.colors)) return [];
  return product.colors
    .map((item: any) => {
      const images: string[] = [];
      if (Array.isArray(item?.images)) {
        images.push(...item.images.filter((img: any) => typeof img === "string" && img.trim()));
      }
      if (typeof item?.image === "string" && item.image.trim()) images.push(item.image);
      return {
        color: String(item?.color || item?.name || "").trim(),
        images: uniqueStrings(images),
      };
    })
    .filter((item: ProductColor) => item.color && item.images.length > 0);
};

const getAllGalleryImages = (product: any, colorOptions: ProductColor[]) => {
  const images: string[] = [];
  const mainImage = getMainImage(product);
  if (mainImage) images.push(mainImage);
  if (Array.isArray(product?.images)) {
    images.push(...product.images.filter((img: any) => typeof img === "string" && img.trim()));
  }
  colorOptions.forEach((color) => { images.push(...color.images); });
  return uniqueStrings(images);
};

const mapSharedProduct = (product: any, link?: any) => ({
  ...product,
  visible: link?.visible !== false,
  featured: !!link?.featured,
  source: "shared" as const,
});

const mapExclusiveProduct = (item: any) => ({
  ...item,
  visible: item?.visible !== false,
  featured: !!item?.featured,
  source: "exclusive" as const,
});

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState("");

  const imageRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [lens, setLens] = useState<LensState>({
    visible: false, left: 0, top: 0, mouseX: 0, mouseY: 0,
  });

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) { setLoading(false); return; }

      try {
        const { data: storeData, error: storeError } = await supabase
          .from("stores").select("id").eq("slug", STORE_SLUG).single();

        if (storeError || !storeData?.id) {
          setProduct(null); setLoading(false); return;
        }

        let foundProduct: any = null;

        // Busca por slug na tabela exclusiva
        const { data: exclusiveBySlug } = await supabase
          .from("ray_melo_products").select("*").eq("slug", slug).maybeSingle();
        if (exclusiveBySlug && isVisible(exclusiveBySlug)) {
          foundProduct = mapExclusiveProduct(exclusiveBySlug);
        }

        // Busca por id na tabela exclusiva
        if (!foundProduct) {
          const { data: exclusiveById } = await supabase
            .from("ray_melo_products").select("*").eq("id", slug).maybeSingle();
          if (exclusiveById && isVisible(exclusiveById)) {
            foundProduct = mapExclusiveProduct(exclusiveById);
          }
        }

        // Busca por slug na tabela compartilhada
        if (!foundProduct) {
          const { data: baseProductBySlug } = await supabase
            .from("ray_melo_products").select("*").eq("slug", slug).maybeSingle();
          if (baseProductBySlug?.id) {
            const { data: linkBySlug } = await supabase
              .from("store_products").select("*")
              .eq("store_id", storeData.id).eq("product_id", baseProductBySlug.id).maybeSingle();
            if (linkBySlug && isVisible(linkBySlug)) {
              foundProduct = mapSharedProduct(baseProductBySlug, linkBySlug);
            }
          }
        }

        // Busca por id na tabela compartilhada
        if (!foundProduct) {
          const { data: baseProductById } = await supabase
            .from("ray_melo_products").select("*").eq("id", slug).maybeSingle();
          if (baseProductById?.id) {
            const { data: linkById } = await supabase
              .from("store_products").select("*")
              .eq("store_id", storeData.id).eq("product_id", baseProductById.id).maybeSingle();
            if (linkById && isVisible(linkById)) {
              foundProduct = mapSharedProduct(baseProductById, linkById);
            }
          }
        }

        setProduct(foundProduct || null);
      } catch (error) {
        console.error("Erro ao buscar produto:", error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  const sizes = useMemo(() => getSizes(product), [product]);
  const colorOptions = useMemo(() => getColorOptions(product), [product]);
  const allGalleryImages = useMemo(() => getAllGalleryImages(product, colorOptions), [product, colorOptions]);

  const galleryImages = useMemo(() => {
    if (colorOptions.length > 0 && selectedColor) {
      const found = colorOptions.find((item) => item.color === selectedColor);
      if (found && found.images.length > 0) return found.images;
    }
    return allGalleryImages;
  }, [colorOptions, selectedColor, allGalleryImages]);

  useEffect(() => {
    if (sizes.length > 0) setSelectedSize(sizes[0]);
    else setSelectedSize("");
  }, [sizes]);

  useEffect(() => {
    if (colorOptions.length > 0) setSelectedColor(colorOptions[0].color);
    else setSelectedColor("");
  }, [colorOptions]);

  useEffect(() => {
    if (galleryImages.length > 0) {
      if (!galleryImages.includes(selectedImage)) setSelectedImage(galleryImages[0]);
    } else {
      setSelectedImage("");
    }
  }, [galleryImages, selectedImage]);

  useEffect(() => {
    const updateSize = () => {
      if (!imageRef.current) return;
      setContainerSize({ width: imageRef.current.clientWidth, height: imageRef.current.clientHeight });
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [selectedImage]);

  const waHref = useMemo(() => {
    if (!product) return "#";
    const message = encodeURIComponent(
      `Olá! Tenho interesse na peça: ${product.name}${selectedSize ? ` | Tamanho: ${selectedSize}` : ""}${selectedColor ? ` | Cor: ${selectedColor}` : ""}.`
    );
    return `https://wa.me/${WA_NUMBER}?text=${message}`;
  }, [product, selectedSize, selectedColor]);

  const handleMouseEnter = () => {
    if (!imageRef.current || !selectedImage) return;
    setContainerSize({ width: imageRef.current.clientWidth, height: imageRef.current.clientHeight });
    setLens((prev) => ({ ...prev, visible: true }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !selectedImage) return;
    const rect = imageRef.current.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;
    const halfLensW = LENS_WIDTH / 2;
    const halfLensH = LENS_HEIGHT / 2;
    const clampedX = Math.max(halfLensW, Math.min(rawX, rect.width - halfLensW));
    const clampedY = Math.max(halfLensH, Math.min(rawY, rect.height - halfLensH));
    setLens({ visible: true, left: clampedX - halfLensW, top: clampedY - halfLensH, mouseX: clampedX, mouseY: clampedY });
  };

  const handleMouseLeave = () => setLens((prev) => ({ ...prev, visible: false }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />
        <div className="px-6 md:px-10 py-24 text-center text-black/40 tracking-[0.2em] text-[11px] uppercase">
          Carregando...
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />
        <div className="px-6 md:px-10 py-24 text-center">
          <p className="text-black/40 tracking-[0.2em] text-[11px] uppercase mb-6">
            Produto não encontrado
          </p>
          <Link
            to="/"
            className="inline-flex items-center border border-black px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-black hover:text-white transition-all whitespace-nowrap"
          >
            Voltar ao Início
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-[#FAF8F5] border-b border-black/8 px-6 md:px-10 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-[11px] text-black/40">
          <Link to="/" className="hover:text-black transition-colors">Início</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <Link to="/catalogo" className="hover:text-black transition-colors">Catálogo</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-black/70 line-clamp-1">{product.name}</span>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-14">
        <section className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-16 items-start">

          {/* LEFT: Gallery */}
          <div>
            <div className="flex lg:flex-row gap-3">
              {/* Vertical thumbs */}
              {galleryImages.length > 1 && (
                <div className="hidden lg:flex flex-col gap-2 w-20 flex-shrink-0">
                  {galleryImages.map((image, index) => {
                    const active = selectedImage === image;
                    return (
                      <button
                        key={`${image}-${index}`}
                        type="button"
                        onClick={() => setSelectedImage(image)}
                        className={`border overflow-hidden flex-shrink-0 cursor-pointer transition-all ${
                          active ? "border-black" : "border-black/10 hover:border-black/30"
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-24 object-cover object-top"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Main image */}
              <div className="flex-1">
                <div
                  ref={imageRef}
                  className="relative overflow-hidden cursor-crosshair bg-[#F0EDE6]"
                  onMouseEnter={handleMouseEnter}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  {selectedImage ? (
                    <img
                      src={selectedImage}
                      alt={product.name}
                      className="w-full h-[480px] md:h-[640px] object-cover object-top select-none"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-[480px] md:h-[640px] bg-[#F0EDE6]" />
                  )}

                  {lens.visible && containerSize.width > 0 && containerSize.height > 0 && selectedImage && (
                    <div
                      className="absolute overflow-hidden border border-white/80 pointer-events-none"
                      style={{
                        left: `${lens.left}px`,
                        top: `${lens.top}px`,
                        width: `${LENS_WIDTH}px`,
                        height: `${LENS_HEIGHT}px`,
                      }}
                    >
                      <img
                        src={selectedImage}
                        alt={product.name}
                        draggable={false}
                        className="absolute object-cover object-top select-none max-w-none"
                        style={{
                          width: `${containerSize.width}px`,
                          height: `${containerSize.height}px`,
                          left: `${-lens.left}px`,
                          top: `${-lens.top}px`,
                          transform: `scale(${ZOOM})`,
                          transformOrigin: `${lens.mouseX}px ${lens.mouseY}px`,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Mobile thumbnails */}
                {galleryImages.length > 1 && (
                  <div className="lg:hidden flex gap-2 mt-3 overflow-x-auto pb-1 [scrollbar-width:none]">
                    {galleryImages.map((image, index) => {
                      const active = selectedImage === image;
                      return (
                        <button
                          key={`mob-${image}-${index}`}
                          type="button"
                          onClick={() => setSelectedImage(image)}
                          className={`border overflow-hidden flex-shrink-0 cursor-pointer transition-all ${
                            active ? "border-black" : "border-black/10"
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${product.name} ${index + 1}`}
                            className="w-16 h-20 object-cover object-top"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Product info */}
          <div className="lg:pt-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-3">
              Ray Melo Boutique
            </p>

            <h1
              className="text-3xl md:text-4xl font-light leading-[1.1] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {product.name}
            </h1>

            <div className="border-t border-black/10 pt-6 space-y-6">
              {/* Color selector */}
              {colorOptions.length > 0 && (
                <div>
                  <p className="text-[11px] tracking-[0.18em] uppercase text-black/50 mb-3">
                    Cor: <span className="text-black font-medium">{selectedColor}</span>
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {colorOptions.map((item) => {
                      const active = selectedColor === item.color;
                      return (
                        <button
                          key={item.color}
                          type="button"
                          onClick={() => setSelectedColor(item.color)}
                          className={`min-w-[70px] h-10 px-4 border text-[11px] tracking-[0.1em] uppercase transition-all cursor-pointer ${
                            active
                              ? "bg-black text-white border-black"
                              : "bg-white text-black border-black/20 hover:border-black/50"
                          }`}
                        >
                          {item.color}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size selector */}
              <div>
                <p className="text-[11px] tracking-[0.18em] uppercase text-black/50 mb-3">
                  Tamanho
                </p>
                {sizes.length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    {sizes.map((size) => {
                      const active = selectedSize === size;
                      return (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setSelectedSize(size)}
                          className={`min-w-[46px] h-10 px-4 border text-[12px] tracking-[0.1em] uppercase transition-all cursor-pointer ${
                            active
                              ? "bg-black text-white border-black"
                              : "bg-white text-black border-black/20 hover:border-black/50"
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[13px] text-black/40">
                    Nenhum tamanho cadastrado para esta peça.
                  </p>
                )}
              </div>

              {/* CTA */}
              <div className="pt-2">
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] text-white h-13 py-4 text-[12px] tracking-[0.18em] uppercase hover:bg-[#20c05c] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Comprar via WhatsApp
                </a>

                <p className="text-[11px] text-black/40 text-center mt-3">
                  Atendimento rápido e seguro pelo WhatsApp
                </p>
              </div>

              {/* Description */}
              {product.description && (
                <div className="border-t border-black/10 pt-6">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-black/40 mb-3">
                    Sobre a peça
                  </p>
                  <p className="text-[13px] leading-7 text-black/65">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="border-t border-black/10 pt-6 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-truck-line text-base text-black/50" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-black">Entrega Rápida</p>
                    <p className="text-[10px] text-black/40">Para todo o Brasil</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-base text-black/50" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-black">Compra Segura</p>
                    <p className="text-[10px] text-black/40">Via WhatsApp</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-go-back-line text-base text-black/50" />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-black">Devolução Grátis</p>
                    <p className="text-[10px] text-black/40">Até 7 dias</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}